/**
 * The booking widget's package notice (boona: docs/codebase/abonements/README.md A5).
 *
 * The public catalogue lists, per service, the packages the salon advertises
 * (`abonement_offers`): `price`, `visits` of THIS service it includes and `basis` —
 * what its other visits cost one by one. The saving is
 *   X = 1 − price ÷ (visits × single-visit price + basis)
 * with the chosen master's price when a master is chosen, else the service's (D8).
 */
import { masterPrice } from "./masterPrice";
import type { ServiceItem } from "./ServiceRegistry";

export type PackageOffer = { type_id: number; title: string; price: number; visits: number; basis: number };

/** A single visit's price: the lower end of a range, so a saving is never overstated. */
function singleVisitPrice(min: number, max: number): number {
  return min > 0 ? min : max;
}

/** The best advertised saving for this service (whole percent, rounded down), or null. */
export function bestPackageOffer(
  service: Pick<ServiceItem, "price_min" | "price_max"> & Partial<Pick<ServiceItem, "staffPrices" | "abonementOffers">>,
  masterId: number | null | undefined,
): { title: string; pct: number } | null {
  const offers = service.abonementOffers ?? [];
  if (!offers.length) return null;
  const p = masterPrice(service, masterId);
  const single = singleVisitPrice(p.min, p.max);
  let best: { title: string; pct: number } | null = null;
  for (const o of offers) {
    const singles = o.visits * single + (o.basis || 0);
    if (!(o.price > 0) || !(singles > 0)) continue;
    // a tenth first (0.1999… is 20 %), then down to a whole percent
    const pct = Math.floor(Math.round((1 - o.price / singles) * 1000) / 10);
    if (pct > 0 && (!best || pct > best.pct)) best = { title: o.title, pct };
  }
  return best;
}
